// Mock content data for DBS landing page

export const navLinks = [
  { label: 'How it works', href: '/#how-it-works' },
  { label: 'Grow your audience', href: '/#grow-your-audience' },
  { label: 'Why partners choose us', href: '/#why-partners' },
  { label: 'Products', href: '/#products' },
  { label: 'Blog', href: '/blog', route: true },
  { label: 'About us', href: '/about', route: true },
];

export const trustedLogos = [
  { name: 'The Register' },
  { name: 'Ziff Davis' },
  { name: 'Informa Tech' },
  { name: 'Madison Logic' },
  { name: 'Future' },
  { name: 'Ymark' },
];

export const problemWalls = [
  {
    title: 'Not enough reach',
    body: 'Your audience is deep but narrow. Delivering lead campaigns at scale from your own database means burning the subscriber base you can never rebuild.',
  },
  {
    title: 'No delivery engine',
    body: 'Data sourcing, outreach, verification, compliance, reporting. Hiring that capability before the revenue exists is a bet you are right not to make.',
  },
  {
    title: 'A team that sells media, not leads',
    body: "Your salespeople are brilliant at sponsorship. Cost-per-lead is a different conversation, and without support it dies in the bottom drawer.",
  },
];

export const modelSteps = [
  {
    step: '01',
    title: 'Package',
    body: 'We help you build a productised lead generation offer under your brand: rate card, campaign specifications, contracts and collateral. Ready to sell in days.',
  },
  {
    step: '02',
    title: 'Deliver',
    body: 'Our team runs everything behind the scenes: data, outreach, content syndication, verification, compliance and reporting. Your client only ever sees your brand.',
  },
  {
    step: '03',
    title: 'Grow',
    body: 'You keep the margin on every campaign. And every campaign captures opt-ins for your brand, so your first-party database and event audiences grow while you earn.',
  },
];

export const upsideStats = [
  { value: '165M+', label: 'B2B professionals reachable on your behalf' },
  { value: '5 Days', label: 'From decision to first campaign live' },
  { value: '100%', label: 'Of the margin stays with you' },
  { value: '2x', label: 'Value from every campaign: revenue and subscribers' },
];

export const whyPartners = [
  {
    title: 'A new revenue line, fast',
    body: 'A turnkey lead generation offer that sells to the advertisers you already know, at margins that make it worth doing. Live in days, profitable from campaign one.',
  },
  {
    title: 'No surprises, ever',
    body: 'Weekly delivery you can set your watch by, live campaign visibility, and proactive flags the moment anything changes. If pacing slips, you hear it from us first — with a plan attached.',
  },
  {
    title: 'We arm your sales team',
    body: 'Battle cards, pricing guidance, pitch support and objection handling, so your team sells lead generation with the same confidence they sell sponsorship. Your people, properly equipped.',
  },
  {
    title: 'Your audience grows with every campaign',
    body: "Every lead we deliver can carry a compliant opt-in for your brand and your events. Your clients get pipeline. You get subscribers. One campaign, two assets.",
  },
];

export const subscriberBullets = [
  "Every campaign carries a compliant opt-in for your brand as well as your client's",
  "Your subscriber list and event audiences grow while your advertisers pay for the campaign",
  "De-risk advertising revenue with an always-on feed of new prospects",
  "Content syndication today, a full content marketing line tomorrow",
];

export const subscriberStats = [
  { value: '100%', label: 'Opt-in rate for your brand' },
  { value: '£0', label: 'Cost to acquire those subscribers' },
  { value: '2x', label: 'Assets built from one campaign' },
];

export const qualityChecks = [
  'Verified contact data',
  'On-specification, every time',
  'Suppression honoured',
  'Automatic replacement',
];

export const enrichIQFeatures = [
  'Automated validation and enrichment on every record',
  'Client specifications enforced before delivery, not after',
  'Live pacing, performance and compliance visibility',
  "Delivery straight into your clients' CRM and automation tools",
];

export const playbookBullets = [
  'The 2026 squeeze: what is really happening to publisher revenue',
  'A worked example: what one campaign contributes',
  'How to protect the audience you already own',
  'Turning every campaign into first-party data and event opt-ins',
];

export const roles = [
  {
    title: 'Publisher Operations',
    body: 'Turn one advertiser into two revenue lines. Own delivery, retain margin, and scale campaigns without new hires or new systems.',
    icon: 'Cog',
  },
  {
    title: 'Commercial Directors',
    body: 'Add a new revenue line to the P&L in weeks. Predictable margin per campaign and honest forecasting your CFO can plan around.',
    icon: 'LineChart',
  },
  {
    title: 'Sales Teams',
    body: 'Sell lead generation with the same confidence you sell sponsorship. Battle cards, pricing guidance and live pitch support.',
    icon: 'Handshake',
  },
  {
    title: 'Marketing & Audience',
    body: 'Every campaign you deliver grows your first-party database. Compliant opt-ins at zero acquisition cost, on someone else\u2019s budget.',
    icon: 'UsersRound',
  },
];

export const caseStudies = [
  {
    brand: 'B2B Tech Publisher',
    tag: 'Case study · 01',
    highlight: '+\u00a3420K',
    highlightLabel: 'New revenue in Year 1',
    metrics: [
      { value: '8 wks', label: 'To first campaign live' },
      { value: '12.4K', label: 'New brand opt-ins added' },
    ],
  },
  {
    brand: 'Finance & Fintech Media',
    tag: 'Case study · 02',
    highlight: '6x',
    highlightLabel: 'Boost in advertiser pipeline',
    metrics: [
      { value: '24 hr', label: 'Lead follow-up time' },
      { value: '25+', label: 'Ops hours saved / campaign' },
    ],
  },
  {
    brand: 'Industrial B2B Publisher',
    tag: 'Case study · 03',
    highlight: '3.2x',
    highlightLabel: 'Advertiser retention',
    metrics: [
      { value: '84%', label: 'Verified opt-in rate' },
      { value: '+267%', label: 'MQL growth YoY' },
    ],
  },
];

export const integrations = [
  'Salesforce',
  'HubSpot',
  'Marketo',
  'Pardot',
  'LinkedIn',
  'ZoomInfo',
  '6sense',
  'Demandbase',
  'ON24',
  'Zapier',
  'Slack',
  'Google Analytics',
];

export const faqs = [
  {
    q: 'How does 5DBS fit into our existing sales process?',
    a: 'Your salespeople keep the advertiser conversation. We equip them with rate cards, battle cards, pricing guidance and pitch support so they can sell a lead generation offer under your brand. Delivery happens quietly behind the scenes.',
  },
  {
    q: 'Do we keep the client relationship?',
    a: 'Every campaign runs under your brand and your paperwork. Your client sees your team, your reporting and your invoice. We are the delivery layer only, and never appear to your advertiser.',
  },
  {
    q: 'How quickly can we launch a lead generation line?',
    a: 'Most partners are live with their first paid campaign inside 5 working days from kick-off. We provide the productised offer, contracts and campaign specifications so nothing needs to be built from scratch.',
  },
  {
    q: 'What happens with data compliance (GDPR / CCPA)?',
    a: 'Consent is enforced at the point of capture on every lead we deliver. Suppression lists are honoured, opt-in records are stored, and full compliance evidence is available for every record we pass to your client.',
  },
  {
    q: 'Can we grow our own audience while running client campaigns?',
    a: 'Yes — that is one of the reasons publishers choose us. Every campaign carries a compliant opt-in for your brand and events alongside your client\u2019s opt-in, so your first-party database and event audiences grow on their budget.',
  },
  {
    q: 'What if a lead fails validation?',
    a: 'It is replaced automatically at no cost. Every lead is validated against your agreed specification before it reaches your client, so rejection disputes, credit conversations and awkward calls simply do not happen.',
  },
];
